import React, { useState, useRef, useEffect } from 'react';
import './SkillsAutocomplete.css';

// Common tech skills for autocomplete
const COMMON_SKILLS = [
  // Programming Languages
  'JavaScript', 'TypeScript', 'Python', 'Java', 'C++', 'C#', 'Go', 'Rust', 'Ruby', 'PHP', 'Swift', 'Kotlin', 'Scala',
  // Frontend
  'React', 'Vue.js', 'Angular', 'Next.js', 'Svelte', 'HTML', 'CSS', 'Tailwind CSS', 'SASS', 'Redux', 'GraphQL',
  // Backend
  'Node.js', 'Express.js', 'Django', 'Flask', 'FastAPI', 'Spring Boot', 'Ruby on Rails', 'ASP.NET', 'NestJS',
  // Databases
  'PostgreSQL', 'MySQL', 'MongoDB', 'Redis', 'SQLite', 'DynamoDB', 'Firebase', 'Supabase', 'Elasticsearch',
  // Cloud & DevOps
  'AWS', 'Google Cloud', 'Azure', 'Docker', 'Kubernetes', 'Terraform', 'CI/CD', 'Jenkins', 'GitHub Actions',
  // Tools
  'Git', 'Linux', 'REST APIs', 'Agile', 'Scrum', 'Jira', 'Figma', 'VS Code',
  // Data & ML
  'Machine Learning', 'Data Science', 'TensorFlow', 'PyTorch', 'Pandas', 'NumPy', 'SQL', 'Data Analysis',
  // Mobile
  'React Native', 'Flutter', 'iOS Development', 'Android Development',
];

function SkillsAutocomplete({
  value = [],
  onChange,
  maxSkills = 10,
  placeholder = 'Type to search skills...',
  label = 'Skills',
}) {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Filter suggestions based on input
  useEffect(() => {
    if (inputValue.trim()) {
      const filtered = COMMON_SKILLS.filter(
        skill =>
          skill.toLowerCase().includes(inputValue.toLowerCase()) &&
          !value.includes(skill)
      ).slice(0, 8);
      setSuggestions(filtered);
      setHighlightedIndex(0);
    } else {
      setSuggestions([]);
    }
  }, [inputValue, value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addSkill = (skill) => {
    if (value.length < maxSkills && !value.includes(skill)) {
      onChange([...value, skill]);
      setInputValue('');
      setSuggestions([]);
      inputRef.current?.focus();
    }
  };

  const removeSkill = (skillToRemove) => {
    onChange(value.filter(skill => skill !== skillToRemove));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (suggestions.length > 0 && isOpen) {
        addSkill(suggestions[highlightedIndex]);
      } else if (inputValue.trim() && value.length < maxSkills) {
        // Add custom skill if not in suggestions
        addSkill(inputValue.trim());
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      removeSkill(value[value.length - 1]);
    }
  };

  return (
    <div className="skills-autocomplete" ref={containerRef}>
      {label && <label className="skills-label">{label}</label>}

      <div className="skills-input-container">
        <div className="selected-skills">
          {value.map(skill => (
            <span key={skill} className="skill-tag">
              {skill}
              <button
                type="button"
                onClick={() => removeSkill(skill)}
                className="skill-remove"
                aria-label={`Remove ${skill}`}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </span>
          ))}

          {value.length < maxSkills && (
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setIsOpen(true);
              }}
              onFocus={() => setIsOpen(true)}
              onKeyDown={handleKeyDown}
              placeholder={value.length === 0 ? placeholder : ''}
              className="skills-input"
            />
          )}
        </div>

        <span className="skills-count">
          {value.length}/{maxSkills}
        </span>
      </div>

      {isOpen && suggestions.length > 0 && (
        <ul className="skills-suggestions">
          {suggestions.map((skill, index) => (
            <li
              key={skill}
              className={`suggestion-item ${index === highlightedIndex ? 'highlighted' : ''}`}
              onClick={() => addSkill(skill)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {skill}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default SkillsAutocomplete;
